/** Typed wrappers over every endpoint the UI uses. */

import { api } from './client'
import type {
  ApplicationDetail,
  Application,
  ApplicationPosition,
  ApplicationSettings,
  ApplicationStats,
  ApplyOptions,
  AnswerAssistMode,
  AutoApplyApplyRequest,
  AutoApplyApplyResult,
  AutoApplySearchRequest,
  AutoApplySearchResult,
  AuthSession,
  BulkApplyResult,
  CheckoutRequest,
  CheckoutResponse,
  CouponValidation,
  DocumentKind,
  ChecklistPayload,
  ContactPayload,
  Country,
  DashboardSummary,
  FetchedJobDescription,
  GeneratedPassword,
  Integration,
  IntegrationSync,
  Job,
  JobFacets,
  JobFilterChoices,
  JobFilterOption,
  JobMatch,
  JobPortal,
  JobSourceSummary,
  JobPortalSync,
  JobPortalUpdate,
  JobPreference,
  JobSourceOption,
  LocationPayload,
  MailAccount,
  MailAlias,
  MailAliasUpdate,
  MailCategory,
  MailMessage,
  MailMessageDetail,
  MailProvider,
  MailStats,
  Pipeline,
  OnboardingState,
  OnboardingStepKey,
  NaukriCreateAccount,
  OtpChallenge,
  PasswordStrength,
  PlansResponse,
  Profile,
  ProfileEnhanceRequest,
  ProfileEnhanceResult,
  ProfileExperience,
  ProfileExperiencePayload,
  UserDocument,
  ProfileRecommendation,
  ProfileSearchResult,
  ReferenceData,
  AgentTraceReport,
  LiveCheckpoint,
  LiveWaiting,
  RehearsalReport,
  Resume,
  ResumeParseResult,
  Skill,
  SubscriptionResponse,
  TokenPair,
  User,
  WorkEligibilityPayload,
  BankEntry,
  ReviewInbox,
  RetryAllResult,
  SubmitReviewAnswersResult,
} from '@/types/api'

export const authApi = {
  register: (body: { email: string; password: string; fullName?: string }) =>
    api.postPublic<AuthSession>('/auth/register', body),
  login: (body: { email: string; password: string }) =>
    api.postPublic<AuthSession>('/auth/login', body),
  refresh: (refreshToken: string) =>
    api.postPublic<TokenPair>('/auth/refresh', { refreshToken }),
  logout: (refreshToken: string) => api.post<{ message: string }>('/auth/logout', { refreshToken }),
  me: () => api.get<User>('/auth/me'),
  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    api.post<{ message: string }>('/auth/change-password', body),
}

export const onboardingApi = {
  getState: () => api.get<OnboardingState>('/onboarding'),
  saveStep: (step: OnboardingStepKey, payload: unknown) =>
    api.put<OnboardingState>(`/onboarding/steps/${step}`, payload),
  saveDraft: (step: OnboardingStepKey, payload: unknown) =>
    api.post<OnboardingState>('/onboarding/draft', { step, payload }),
  skip: (step: OnboardingStepKey, reason?: string) =>
    api.post<OnboardingState>('/onboarding/skip', { step, reason }),
  navigate: (step: OnboardingStepKey) =>
    api.post<OnboardingState>(`/onboarding/navigate/${step}`),
  complete: () =>
    api.post<{ isComplete: boolean; completedAt: string; redirectTo: string }>(
      '/onboarding/complete',
    ),
  checkPassword: (password: string) =>
    api.post<PasswordStrength>('/onboarding/password-strength', { password }),
  generatePassword: (length = 16) =>
    api.get<GeneratedPassword>('/onboarding/generate-password', { length }),
  getSettings: () => api.get<ApplicationSettings>('/onboarding/settings'),
  saveSettings: (body: Partial<ApplicationSettings>) =>
    api.put<ApplicationSettings>('/onboarding/settings', body),
}

export const profileApi = {
  get: () => api.get<Profile>('/profile'),
  /** The answer bank: every screening question answered before. */
  answers: () => api.get<BankEntry[]>('/profile/answers'),
  forgetAnswer: (questionText: string) =>
    api.delete<{ forgotten: boolean }>(`/profile/answers?questionText=${encodeURIComponent(questionText)}`),
  /** The papers forms ask for besides the résumé (a photo, an ID proof…), uploaded once. */
  listDocuments: () => api.get<UserDocument[]>('/profile/documents'),
  uploadDocument: (kind: DocumentKind, file: File) => {
    const form = new FormData()
    form.append('kind', kind)
    form.append('file', file)
    return api.upload<UserDocument>('/profile/documents', form, { kind })
  },
  deleteDocument: (documentId: string) => api.delete<{ message: string }>(`/profile/documents/${documentId}`),
  updateIdentity: (body: Record<string, unknown>) =>
    api.patch<Profile>('/profile/identity', body),
  updateLocation: (body: LocationPayload) => api.put<LocationPayload>('/profile/location', body),
  updateContact: (body: ContactPayload) => api.put<ContactPayload>('/profile/contact', body),
  updateWorkEligibility: (body: WorkEligibilityPayload) =>
    api.put<WorkEligibilityPayload>('/profile/work-eligibility', body),
  updateChecklist: (body: ChecklistPayload) =>
    api.put<ChecklistPayload>('/profile/checklist', body),
  updatePreferences: (body: Partial<JobPreference>) =>
    api.put<JobPreference>('/profile/preferences', body),
  updateSkills: (
    skills: Array<{ name: string; yearsOfExperience?: number | null; isPrimary?: boolean }>,
  ) =>
    api.put<Skill[]>('/profile/skills', { skills }),
  removeSkill: (skillId: string) => api.delete<{ message: string }>(`/profile/skills/${skillId}`),
  /** Replaces every role on the profile. */
  updateExperiences: (experiences: ProfileExperiencePayload[]) =>
    api.put<ProfileExperience[]>('/profile/experiences', { experiences }),
  recommendations: () => api.get<ProfileRecommendation[]>('/profile/recommendations'),
  /** AI rewrites a role description, summary or headline for ATS and recruiters; nothing is saved. */
  enhance: (body: ProfileEnhanceRequest) =>
    api.post<ProfileEnhanceResult>('/profile/enhance', body),
}

export const resumeApi = {
  list: () => api.get<Resume[]>('/resumes'),
  upload: (
    file: File,
    options?: { makeDefault?: boolean; applyToProfile?: boolean; applySkills?: boolean },
  ) => {
    const form = new FormData()
    form.append('file', file)
    return api.upload<ResumeParseResult>('/resumes', form, {
      makeDefault: options?.makeDefault ?? true,
      applyToProfile: options?.applyToProfile ?? true,
      applySkills: options?.applySkills ?? true,
    })
  },
  setDefault: (resumeId: string) => api.post<Resume>(`/resumes/${resumeId}/default`),
  remove: (resumeId: string) => api.delete<{ message: string }>(`/resumes/${resumeId}`),
  // A plain link cannot carry the bearer token, so downloads go through fetch.
  download: (resumeId: string, fileName: string) =>
    api.download(`/resumes/${resumeId}/download`, fileName),
}

export const integrationApi = {
  list: () => api.get<Integration[]>('/integrations'),
  getNaukri: () => api.get<Integration | null>('/integrations/naukri'),
  connectNaukri: (body: { identifier: string; password: string }) =>
    api.post<Integration>('/integrations/naukri/connect', body),
  startOtp: (mobileNumber: string) =>
    api.post<OtpChallenge>('/integrations/naukri/otp/start', { mobileNumber }),
  verifyOtp: (body: { challengeId: string; code: string }) =>
    api.post<Integration>('/integrations/naukri/otp/verify', body),
  createAccount: (body: { mobileNumber?: string; fullName?: string }) =>
    api.post<NaukriCreateAccount>('/integrations/naukri/create-account', body),
  sync: (limit = 25) => api.post<IntegrationSync>(`/integrations/naukri/sync?limit=${limit}`),
  disconnect: (provider: string) => api.delete<{ message: string }>(`/integrations/${provider}`),
}

export interface JobSearchParams {
  q?: string
  searchMode?: string
  exclude?: string
  sources?: string[]
  /** Job portal keys (`JobPortal.key`). */
  boards?: string[]
  /** Places, ORed: a city or any text, or a whole country as `country:IN`. */
  locations?: string[]
  /** With `locations`: also remote jobs open in this country (two-letter code). */
  remoteIn?: string
  companies?: string[]
  /** Companies to hide. */
  excludeCompanies?: string[]
  workplaceTypes?: string[]
  employmentTypes?: string[]
  /** `JobFilterChoices.roles` values. */
  roles?: string[]
  /** `JobFilterChoices.seniorities` values. */
  seniorities?: string[]
  /** The seeker's highest degree: hides jobs that ask for more. */
  degreeLevel?: string
  maxExperienceYears?: number
  sponsorsVisa?: boolean
  postedWithinDays?: number
  easyApplyOnly?: boolean
  page?: number
  pageSize?: number
}

/** `boards` travels as one comma list (`?boards=indeed,reed_uk`), unlike the repeated `sources`. */
const boardList = (boards?: string[]) => (boards?.length ? boards.join(',') : undefined)

export const jobApi = {
  careerPaths: () => api.get<Array<{ title: string; reason: string; kind: 'parallel' | 'progression'; needsConfirmation: boolean }>>('/jobs/career-paths'),
  search: (params: JobSearchParams) =>
    api.getPage<Job>('/jobs', { ...params, boards: boardList(params.boards) }),
  facets: () => api.get<JobFacets>('/jobs/facets'),
  /** What a filter's search box offers: places (countries, then cities) or companies. */
  filterOptions: (params: { field: 'locations' | 'companies'; q?: string; country?: string; limit?: number }) =>
    api.get<JobFilterOption[]>('/jobs/filter-options', params),
  /** The fixed choices of the role, seniority and degree filters. */
  filterChoices: () => api.get<JobFilterChoices>('/jobs/filter-choices'),
  /** `filters` are the browse filters; their `sources` / `boards` are ignored in favour of the arguments. */
  topMatches: (limit = 5, minScore = 0, sources?: string[], boards?: string[], filters?: JobSearchParams) =>
    api.get<JobMatch[]>('/jobs/top-matches', {
      ...filters,
      page: undefined,
      pageSize: undefined,
      limit,
      minScore,
      sources,
      boards: boardList(boards),
    }),
  sources: () => api.get<JobSourceOption[]>('/jobs/sources'),
  recommendations: (params: {
    page?: number
    pageSize?: number
    minScore?: number
    includePassed?: boolean
    savedOnly?: boolean
  }) => api.getPage<JobMatch>('/jobs/recommendations', params as Record<string, unknown>),
  get: (jobId: string) => api.get<Job>(`/jobs/${jobId}`),
  save: (jobId: string) => api.post<JobMatch>(`/jobs/${jobId}/save`),
  pass: (jobId: string, reason?: string) =>
    api.post<JobMatch>(`/jobs/${jobId}/pass`, { reason }),
  rematch: () => api.post<{ message: string }>('/jobs/rematch'),
  /** Search company career pages with the saved preferences; keeps the best `limit`. */
  profileSearch: (body: { limit?: number; minScore?: number } = {}) =>
    api.post<ProfileSearchResult>('/jobs/profile-search', body),
  fetchDescription: (jobUrl: string) =>
    api.post<FetchedJobDescription>('/jobs/fetch-description', { jobUrl }),
  addByUrl: (body: { jobUrl: string; jobDescription?: string }) =>
    api.post<JobMatch>('/jobs/add-by-url', body),
  /** Auto Apply: every ready match for the filters, paged, with totals and widen-search suggestions. */
  autoApplySearch: (body: AutoApplySearchRequest) =>
    api.post<AutoApplySearchResult>('/jobs/auto-apply/search', body),
  /** Auto Apply: queue the top `maxJobs` ready jobs for the filters, or the `jobIds` picked. */
  autoApplyApply: (body: AutoApplyApplyRequest) =>
    api.post<AutoApplyApplyResult>('/jobs/auto-apply/apply', body),
}

/** The portals the user lets us use for them: account, profile, feed and apply. */
export const jobPortalApi = {
  list: () => api.get<JobPortal[]>('/job-portals'),
  /** Matches and applications per source: Naukri, each portal, and links you added. */
  summary: () => api.get<JobSourceSummary[]>('/job-portals/summary'),
  update: (key: string, body: JobPortalUpdate) =>
    api.put<JobPortal>(`/job-portals/${encodeURIComponent(key)}`, body),
  add: (body: { url: string; name?: string }) => api.post<JobPortal>('/job-portals', body),
  remove: (key: string) =>
    api.delete<{ message: string }>(`/job-portals/${encodeURIComponent(key)}`),
  /** Starts account creation / sign-in in the background; the status becomes `connecting`. */
  connect: (key: string) =>
    api.post<JobPortal>(`/job-portals/${encodeURIComponent(key)}/connect`),
  disconnect: (key: string) =>
    api.post<JobPortal>(`/job-portals/${encodeURIComponent(key)}/disconnect`),
  /** `queued` in the result means the fetch carries on in the background. */
  sync: (key: string) =>
    api.post<JobPortalSync>(`/job-portals/${encodeURIComponent(key)}/sync`),
  /**
   * The connection's page parked for the user (a CAPTCHA, a human check, a
   * phone code): status, picture, the click/type relay, and "I have finished".
   */
  handoff: (key: string) =>
    api.get<LiveCheckpoint>(`/job-portals/${encodeURIComponent(key)}/handoff`),
  handoffScreenshot: (key: string) =>
    api.blob(`/job-portals/${encodeURIComponent(key)}/handoff/screenshot`),
  handoffClick: (key: string, x: number, y: number) =>
    api.post<{ message: string }>(`/job-portals/${encodeURIComponent(key)}/handoff/click`, { x, y }),
  handoffType: (key: string, text: string) =>
    api.post<{ message: string }>(`/job-portals/${encodeURIComponent(key)}/handoff/type`, { text }),
  handoffDone: (key: string) =>
    api.post<{ message: string }>(`/job-portals/${encodeURIComponent(key)}/handoff/done`),
}

export const applicationApi = {
  create: (body: { jobId: string; resumeId?: string; notes?: string } & ApplyOptions) =>
    api.post<ApplicationDetail>('/applications', body),
  bulkApply: (jobIds: string[], options: ApplyOptions = {}) =>
    api.post<BulkApplyResult>('/applications/bulk', { jobIds, ...options }),
  list: (params: { page?: number; pageSize?: number; statuses?: string[]; search?: string }) =>
    api.getPage<Application>('/applications', params as Record<string, unknown>),
  reliability: () => api.get<{ cohortDays: number; submittedToday: number; submitted: number; failed: number; needsReview: number; inProgress: number; submissionRate: number | null; byPortal: Array<{ portal: string; submitted: number; failed: number; needsReview: number; inProgress: number; submissionRate: number | null }> }>('/applications/reliability'),
  stats: () => api.get<ApplicationStats>('/applications/stats'),
  dashboard: () => api.get<DashboardSummary>('/applications/dashboard'),
  pipeline: () => api.get<Pipeline>('/applications/pipeline'),
  get: (id: string) => api.get<ApplicationDetail>(`/applications/${id}`),
  position: (id: string, statuses?: string[]) =>
    api.get<ApplicationPosition>(`/applications/${id}/position`, { statuses }),
  submitScreening: (
    id: string,
    answers: Array<{ questionKey: string; answerValue: string | null }>,
    submit = true,
  ) => api.post<ApplicationDetail>(`/applications/${id}/screening`, { answers, submit }),
  update: (id: string, body: Record<string, unknown>) =>
    api.patch<ApplicationDetail>(`/applications/${id}`, body),
  retry: (id: string) => api.post<ApplicationDetail>(`/applications/${id}/retry`),
  /** Every waiting application's open questions, grouped, with what each application waits for. */
  reviewInbox: () => api.get<ReviewInbox>('/applications/review'),
  /** One answer per question for every application asking it; what is then complete is queued. */
  submitReviewAnswers: async (answers: Array<{ groupKey: string; answerValue: string | null }>, submit = true): Promise<SubmitReviewAnswersResult> => {
    // Large inboxes exceed the server's 200-answer payload limit. Save in bounded
    // chunks and queue only with the last chunk, after every answer was saved.
    const chunks = answers.length ? Array.from({ length: Math.ceil(answers.length / 200) }, (_, i) => answers.slice(i * 200, (i + 1) * 200)) : [[]]
    let answered = 0
    const unmatched: SubmitReviewAnswersResult['unmatched'] = []
    let result: SubmitReviewAnswersResult | undefined
    for (const [index, chunk] of chunks.entries()) {
      result = await api.post<SubmitReviewAnswersResult>('/applications/review/answers', { answers: chunk, submit: submit && index === chunks.length - 1 })
      answered += result.answered
      unmatched.push(...result.unmatched)
    }
    return { ...result!, answered, unmatched }
  },
  /** AI drafts one inbox answer, against the first application asking it. */
  reviewAssist: (body: { groupKey: string; mode: AnswerAssistMode; text?: string }) =>
    api.post<{ text: string }>('/applications/review/assist', body),
  /** Submit the waiting applications whose details are all filled (all of them, or `ids`). */
  submitReady: (ids?: string[]) =>
    api.post<{ queued: string[] }>('/applications/submit-ready', { applicationIds: ids ?? null }),
  /** Every stopped application goes back on the queue, whatever it waits for; the "may have submitted" ones only with `includeUnconfirmed`. */
  retryAll: (includeUnconfirmed: boolean) =>
    api.post<RetryAllResult>('/applications/retry-all', { includeUnconfirmed }),
  cancel: (id: string) => api.delete<{ message: string }>(`/applications/${id}`),
  /** AI writes (draft) or polishes (improve) one screening answer; nothing is saved. */
  assistAnswer: (id: string, body: { questionKey: string; mode: AnswerAssistMode; text?: string }) =>
    api.post<{ text: string }>(`/applications/${id}/answers/assist`, body),
  downloadTailoredResume: (id: string, fileName: string) =>
    api.download(`/applications/${id}/tailored-resume`, fileName),
  /** Test run on the employer's site: everything but Submit (and no account sign-up). */
  rehearse: (id: string) => api.post<ApplicationDetail>(`/applications/${id}/rehearse`),
  rehearseJob: (jobId: string) => api.post<ApplicationDetail>('/applications/rehearse', { jobId }),
  rehearsal: (id: string) => api.get<RehearsalReport>(`/applications/${id}/rehearsal`),
  /** A live page parked for the user (CAPTCHA): status, picture, and the relay. */
  live: (id: string) => api.get<LiveCheckpoint>(`/applications/${id}/live`),
  /** Applications a CAPTCHA stopped (nothing was sent), and running them again. */
  captchaStopped: () => api.get<{ count: number }>('/applications/captcha-stopped'),
  retryCaptchaStopped: () =>
    api.post<{ queued: string[] }>('/applications/captcha-stopped/retry'),
  /** Every run of mine parked on a CAPTCHA right now. */
  liveWaiting: () => api.get<LiveWaiting[]>('/applications/live'),
  liveScreenshot: (id: string) => api.blob(`/applications/${id}/live/screenshot`),
  /** Where a run ended: the confirmation page, or the screen it stopped on. */
  screenshot: (id: string, name: string) =>
    api.blob(`/applications/${id}/screenshots/${encodeURIComponent(name)}`),
  liveClick: (id: string, x: number, y: number) =>
    api.post<{ message: string }>(`/applications/${id}/live/click`, { x, y }),
  liveScroll: (id: string, dy: number) => api.post<{ message: string }>(`/applications/${id}/live/scroll`, { dy }),
  liveReveal: (id: string) => api.post<{ message: string }>(`/applications/${id}/live/reveal`, {}),
  /** "I've finished what the page asked": the run carries on from the same screen. */
  liveDone: (id: string) => api.post<{ message: string }>(`/applications/${id}/live/done`, {}),
  liveType: (id: string, text: string) =>
    api.post<{ message: string }>(`/applications/${id}/live/type`, { text }),
  /** What the repair/navigator agent saw and did, turn by turn. */
  agentTrace: (id: string) => api.get<AgentTraceReport>(`/applications/${id}/agent-trace`),
  agentTraceFile: (id: string, name: string) =>
    api.blob(`/applications/${id}/agent-trace/${encodeURIComponent(name)}`),
  downloadAgentTraceFile: (id: string, name: string) =>
    api.download(`/applications/${id}/agent-trace/${encodeURIComponent(name)}`, name),
  rehearsalFile: (id: string, name: string) =>
    api.blob(`/applications/${id}/rehearsal/${encodeURIComponent(name)}`),
  downloadRehearsalFile: (id: string, name: string) =>
    api.download(`/applications/${id}/rehearsal/${encodeURIComponent(name)}`, name),
  /** The portal account's email and password; `password` is the person's own login password. */
  revealAccountPassword: (id: string, password: string) =>
    api.post<{ email: string | null; password: string; source: 'application' | 'naukri' | 'settings' }>(`/applications/${id}/account-password`, { password }),
}

export interface MailListParams {
  category?: MailCategory
  search?: string
  page?: number
  pageSize?: number
  applicationId?: string
}

export const mailApi = {
  alias: () => api.get<MailAlias>('/mail/alias'),
  updateAlias: (body: MailAliasUpdate) => api.put<MailAlias>('/mail/alias', body),
  messages: (params: MailListParams) =>
    api.getPage<MailMessage>('/mail/messages', params as Record<string, unknown>),
  message: (id: string) => api.get<MailMessageDetail>(`/mail/messages/${id}`),
  markRead: (id: string) => api.post<unknown>(`/mail/messages/${id}/read`),
  markAllRead: () => api.post<unknown>('/mail/messages/read-all'),
  stats: () => api.get<MailStats>('/mail/stats'),
  accounts: () => api.get<MailAccount[]>('/mail/accounts'),
  connect: (provider: MailProvider) =>
    api.get<{ authUrl: string }>(`/mail/accounts/${provider}/connect`),
  disconnect: (id: string) => api.delete<unknown>(`/mail/accounts/${id}`),
  sync: (id: string) => api.post<unknown>(`/mail/accounts/${id}/sync`),
}

export const referenceApi = {
  all: () => api.get<ReferenceData>('/reference'),
  countries: (q?: string) => api.get<Country[]>('/reference/countries', { q }),
  states: (countryCode: string) =>
    api.get<Array<{ code: string; name: string }>>(
      `/reference/countries/${countryCode}/states`,
    ),
}

/** Plans, the user's subscription, and Stripe checkout / billing portal. */
export const billingApi = {
  /** Public: the plans, prices per billing period, and whether payment is open. */
  plans: () => api.get<PlansResponse>('/billing/plans'),
  subscription: () => api.get<SubscriptionResponse>('/billing/subscription'),
  /** `url` → send the browser to Stripe Checkout; `changed` → the existing subscription moved plan. */
  checkout: (body: CheckoutRequest) => api.post<CheckoutResponse>('/billing/checkout', body),
  /** Check a partner coupon code before checkout; an unknown or inactive code answers `valid: false` with a message. */
  validateCoupon: (code: string) => api.post<CouponValidation>('/billing/coupon/validate', { code }),
  /** Stripe's own page to update the card, see invoices, or cancel. */
  portal: () => api.post<{ url: string }>('/billing/portal'),
}
